import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'title',              label: 'Practice name',          type: 'input',    placeholder: 'Raum Architects' },
  { key: 'tagline',            label: 'Hero tagline',           type: 'input',    placeholder: 'We create space.' },
  { key: 'intro_text',         label: 'Hero intro paragraph',   type: 'textarea', rows: 3 },
  { key: 'philosophy_heading', label: 'Philosophy heading',     type: 'input' },
  { key: 'philosophy_text',    label: 'Philosophy body',        type: 'textarea', rows: 6,
    help: 'Use a blank line to separate paragraphs.' },
  { key: 'director_text',      label: 'Direction / principal',  type: 'textarea', rows: 4,
    help: 'Shown in a two-column block above Design Principles.' },
  { key: 'team_text',          label: 'Team & network',         type: 'textarea', rows: 4 },
  { key: 'practice_heading',   label: 'Practice section heading', type: 'input' },
  { key: 'practice_text',      label: 'Practice section body',  type: 'textarea', rows: 5 },
];

export default function AboutEditor() {
  const [content, setContent] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/about');
      setContent(data);
      setOriginal(data);
    } catch {
      toast.error('Failed to load About content');
    }
  };

  useEffect(() => { load(); }, []);

  if (!content) {
    return (
      <div className="bg-white rounded-sm border border-black/10 p-6 flex items-center gap-2 text-sm text-brand-dark/50">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading About content...
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== JSON.stringify(original);

  const update = (key, value) => setContent(c => ({ ...c, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/studio/about', content);
      const updated = data.about_content || content;
      setContent(updated);
      setOriginal(updated);
      toast.success('About page updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="about-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-sm font-medium text-brand-dark">About Page</h3>
          <p className="text-xs text-brand-dark/50 font-body mt-1">
            Edits go live on the public About page immediately after saving.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <Button variant="ghost" size="sm" onClick={() => setContent(original)}
              data-testid="about-reset" className="text-xs">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Discard
            </Button>
          )}
          <Button onClick={save} disabled={!isDirty || saving} size="sm"
            data-testid="about-save"
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-3.5 h-3.5 mr-1.5" /> Save</>)}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {FIELDS.map(f => (
          <div key={f.key} data-testid={`about-field-${f.key}`}>
            <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">
              {f.label}
            </label>
            {f.type === 'textarea' ? (
              <Textarea
                value={content[f.key] || ''}
                onChange={(e) => update(f.key, e.target.value)}
                rows={f.rows || 4}
                placeholder={f.placeholder}
                className="rounded-sm font-body text-sm"
                data-testid={`about-input-${f.key}`}
              />
            ) : (
              <Input
                value={content[f.key] || ''}
                onChange={(e) => update(f.key, e.target.value)}
                placeholder={f.placeholder}
                className="rounded-sm h-10 font-body text-sm"
                data-testid={`about-input-${f.key}`}
              />
            )}
            {f.help && <p className="text-[11px] text-brand-dark/40 font-body mt-1">{f.help}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
